import React from 'react';
import { Link } from 'react-router-dom';

/* A small, safe Markdown renderer for the assistant's answers.
 *
 * The text comes from a model, so it is never handed to dangerouslySetInnerHTML:
 * everything below builds React elements, and React escapes every string. It covers
 * what the assistant writes: paragraphs, ### headings, - and 1. lists (one level of
 * nesting), **bold**, *italic*, `code`, ``` fences, > quotes, pipe tables, --- rules
 * and [links](url). Links only open http(s) or an in-app path. Rack and trough plant
 * IDs (A231, T128) become links to the plant's page; aero IDs (F6) are left as text
 * because two characters match too much ordinary writing.
 *
 * It re-parses the whole answer on every streamed chunk. Answers are a few KB, so
 * that is cheap, and an unfinished line simply renders as text until it completes. */

const FENCE = /^\s*```/;
const HEADING = /^(#{1,6})\s+(.*)$/;
const BULLET = /^(\s*)([-*•+])\s+(.*)$/;
const ORDERED = /^(\s*)(\d{1,3})[.)]\s+(.*)$/;
const QUOTE = /^\s*>\s?(.*)$/;
const RULE = /^\s*([-*_])(\s*\1){2,}\s*$/;
const TABLE_ROW = /^\s*\|.*\|\s*$/;
const TABLE_SEP = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;

const startsBlock = (line, next) =>
  FENCE.test(line) || HEADING.test(line) || BULLET.test(line) || ORDERED.test(line)
  || QUOTE.test(line) || RULE.test(line) || (TABLE_ROW.test(line) && TABLE_SEP.test(next || ''));

const cells = row => row.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim());

function parseBlocks(src) {
  const lines = String(src || '').replace(/\r\n?/g, '\n').split('\n');
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i += 1; continue; }

    if (FENCE.test(line)) {
      const body = [];
      i += 1;
      while (i < lines.length && !FENCE.test(lines[i])) { body.push(lines[i]); i += 1; }
      i += 1; // the closing fence (absent while streaming: fine)
      blocks.push({ type: 'code', text: body.join('\n') });
      continue;
    }

    let m = line.match(HEADING);
    if (m) { blocks.push({ type: 'heading', level: m[1].length, text: m[2].replace(/\s*#+\s*$/, '') }); i += 1; continue; }

    if (RULE.test(line)) { blocks.push({ type: 'rule' }); i += 1; continue; }

    if (TABLE_ROW.test(line) && TABLE_SEP.test(lines[i + 1] || '')) {
      const head = cells(line);
      const align = cells(lines[i + 1]).map(c => (c.endsWith(':') ? 'right' : null));
      const rows = [];
      i += 2;
      while (i < lines.length && TABLE_ROW.test(lines[i])) { rows.push(cells(lines[i])); i += 1; }
      blocks.push({ type: 'table', head, align, rows });
      continue;
    }

    if (BULLET.test(line) || ORDERED.test(line)) {
      const ordered = !BULLET.test(line);
      const items = [];
      let start = 1;
      while (i < lines.length) {
        const l = lines[i];
        const b = l.match(BULLET);
        const o = l.match(ORDERED);
        const hit = b || o;
        if (hit) {
          // A list of the other kind at the top level starts a new block.
          if (!hit[1].length && (ordered ? !!b : !!o) && items.length) break;
          if (!items.length && o) start = Number(o[2]);
          items.push({ level: hit[1].replace(/\t/g, '  ').length >= 2 ? 1 : 0, text: hit[3] });
          i += 1;
        } else if (l.trim() && /^\s{2,}/.test(l) && items.length) {
          items[items.length - 1].text += ` ${l.trim()}`; // wrapped continuation
          i += 1;
        } else break;
      }
      blocks.push({ type: ordered ? 'ol' : 'ul', start, items });
      continue;
    }

    if (QUOTE.test(line)) {
      const body = [];
      while (i < lines.length && QUOTE.test(lines[i])) { body.push(lines[i].match(QUOTE)[1]); i += 1; }
      blocks.push({ type: 'quote', lines: body });
      continue;
    }

    const para = [line];
    i += 1;
    while (i < lines.length && lines[i].trim() && !startsBlock(lines[i], lines[i + 1])) { para.push(lines[i]); i += 1; }
    blocks.push({ type: 'p', lines: para });
  }
  return blocks;
}

// Order matters: code first (nothing inside it is formatted), then links, bold, italic, IDs.
const INLINE = /(`[^`\n]+`)|(\[[^\]\n]+\]\([^)\s]+\))|(\*\*[^*\n]+?\*\*|__[^_\n]+?__)|(\*[^*\s\n][^*\n]*?\*)|\b([ABC][1-4][1-4][1-7]|T[1-4][12][1-9])\b/g;

const safeHref = href => {
  if (/^https?:\/\//i.test(href)) return { external: true, href };
  if (/^\/(?!\/)/.test(href)) return { external: false, href };
  return null;
};

function inline(text, keyBase = 'i') {
  const out = [];
  let last = 0;
  let n = 0;
  const str = String(text);
  str.replace(INLINE, (match, code, link, bold, italic, plant, offset) => {
    if (offset > last) out.push(str.slice(last, offset));
    const key = `${keyBase}-${n += 1}`;
    if (code) {
      out.push(<code key={key}>{code.slice(1, -1)}</code>);
    } else if (link) {
      const [, label, href] = link.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
      const target = safeHref(href);
      if (!target) out.push(label);
      else if (target.external) out.push(<a key={key} href={target.href} target="_blank" rel="noopener noreferrer">{inline(label, key)}</a>);
      else out.push(<Link key={key} to={target.href}>{inline(label, key)}</Link>);
    } else if (bold) {
      out.push(<strong key={key}>{inline(bold.slice(2, -2), key)}</strong>);
    } else if (italic) {
      out.push(<em key={key}>{inline(italic.slice(1, -1), key)}</em>);
    } else if (plant) {
      out.push(<Link key={key} to={`/plant/${plant}`} title={`Open plant ${plant}`}>{plant}</Link>);
    }
    last = offset + match.length;
    return match;
  });
  if (last < str.length) out.push(str.slice(last));
  return out;
}

// Right-align cells that are figures ("6.2", "1,240 W", "-3%", "14 d") so columns of numbers line up.
const NUMERIC = /^[-+−]?[\d.,]+\s*(%|°C|°|ppm|mS\/cm|mS|kWh|kW|W|V|A|L|ml|g|d|h|min|units?)?$/i;

export default function Markdown({ text }) {
  const blocks = parseBlocks(text);
  return blocks.map((b, bi) => {
    const k = `b${bi}`;
    switch (b.type) {
      case 'heading':
        return b.level <= 2
          ? <h3 key={k}>{inline(b.text, k)}</h3>
          : <h4 key={k}>{inline(b.text, k)}</h4>;
      case 'code':
        return <pre key={k}><code>{b.text}</code></pre>;
      case 'rule':
        return <hr key={k} />;
      case 'quote':
        return <blockquote key={k}>{b.lines.map((l, li) => <React.Fragment key={li}>{li > 0 && <br />}{inline(l, `${k}-${li}`)}</React.Fragment>)}</blockquote>;
      case 'ul':
      case 'ol': {
        const Tag = b.type;
        return (
          <Tag key={k} start={b.type === 'ol' && b.start !== 1 ? b.start : undefined}>
            {b.items.map((it, ii) => <li key={ii} data-level={it.level}>{inline(it.text, `${k}-${ii}`)}</li>)}
          </Tag>
        );
      }
      case 'table': {
        const width = Math.max(b.head.length, ...b.rows.map(r => r.length));
        const alignOf = (ci) => {
          if (b.align[ci]) return b.align[ci];
          const col = b.rows.map(r => r[ci]).filter(Boolean);
          return col.length && col.every(c => NUMERIC.test(c.replace(/\*\*/g, ''))) ? 'right' : undefined;
        };
        const aligns = Array.from({ length: width }, (_, ci) => alignOf(ci));
        return (
          <div key={k} className="table" role="region" aria-label="Table" tabIndex={0}>
            <table>
              <thead><tr>{Array.from({ length: width }, (_, ci) => <th key={ci} style={{ textAlign: aligns[ci] }}>{inline(b.head[ci] || '', `${k}-h${ci}`)}</th>)}</tr></thead>
              <tbody>
                {b.rows.map((r, ri) => (
                  <tr key={ri}>{Array.from({ length: width }, (_, ci) => <td key={ci} style={{ textAlign: aligns[ci] }}>{inline(r[ci] || '', `${k}-${ri}-${ci}`)}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
      default:
        return <p key={k}>{b.lines.map((l, li) => <React.Fragment key={li}>{li > 0 && <br />}{inline(l, `${k}-${li}`)}</React.Fragment>)}</p>;
    }
  });
}

/** The answer as plain text, for the screen-reader announcement and Copy. */
export const plainText = text => String(text || '')
  .replace(/```[^\n]*\n?/g, '')
  .replace(/^\s*#{1,6}\s+/gm, '')
  .replace(/\*\*([^*]+)\*\*/g, '$1')
  .replace(/`([^`]+)`/g, '$1')
  .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
  .trim();
